import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import './App.css';

// Components (to be created)
import Auth from './components/Auth/Auth';
import Dashboard from './components/Dashboard/Dashboard';
import SquadManager from './components/SquadManager/SquadManager';
import SessionView from './components/SessionView/SessionView';
import MapDisplay from './components/MapDisplay/MapDisplay';
import FPSView from './components/FPSView/FPSView';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';

// Context
import { SettingsProvider } from './contexts/SettingsContext';

// Services
import { getToken, getCurrentUser } from './services/api';

// Wrapper component to handle authentication flow with returnUrl
function AuthWrapper({ onLogin }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const handleLoginSuccess = (userData, token) => {
    onLogin(userData, token);
    
    // Check if there's a returnUrl to redirect to
    const returnUrl = searchParams.get('returnUrl');
    if (returnUrl) {
      console.log('[AuthWrapper] Redirecting to returnUrl:', returnUrl);
      navigate(decodeURIComponent(returnUrl), { replace: true });
    } else {
      console.log('[AuthWrapper] No returnUrl, staying on dashboard');
    }
  };
  
  return <Auth onLogin={handleLoginSuccess} />;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const token = getToken();
      if (token) {
        try {
          const userData = await getCurrentUser();
          setUser(userData);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Failed to get current user:', error);
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
    
    // Listen for storage changes (e.g., token removal from 401 errors)
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        console.log('[App] Token removed, logging out');
        setIsAuthenticated(false);
        setUser(null);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading FPSTAK...</p>
      </div>
    );
  }

  return (
    <SettingsProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public route - Login/Register */}
            <Route 
              path="/" 
              element={
                !isAuthenticated ? (
                  <AuthWrapper onLogin={handleLogin} />
                ) : (
                  <Dashboard user={user} onLogout={handleLogout} />
                )
              } 
            />
            
            {/* Protected routes */}
            <Route 
              path="/squads" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <SquadManager user={user} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/session/:id" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <SessionView user={user} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/map/:sessionId" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <MapDisplay user={user} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/fps/:sessionId" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <FPSView user={user} />
                </ProtectedRoute>
              } 
            />
            
            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </SettingsProvider>
  );
}

export default App;

